import React, { Component } from "react"
import PropTypes from "prop-types"
import { queryMyMazdaVinRego, linkMyMazdaVinRego } from "apis/mymazda"
import COLOR_PALETTE from "constants/color-palette"
import { BASE_HEADING } from "constants/general-style"
import { connect } from "react-redux"
import { actionCreators } from "store/actions/mymazda"
import { bindAll, onKeyPress } from "utils"
import { withRadium, DetectCompact } from "higher-order-components"
import { MYMAZDA_ERRORS } from "constants/errors"
import {
  Summary,
  SteppedFlow,
  WysiwygContent,
  TickModalIcon,
  Hyperlink,
  InputText,
  ExpandableArea
} from "components"
import { H3, Space, Button, Modal } from "AtomicComponents"
import { AlertList } from "AtomicContainers"
import { isLocalStorageSupported, removeLocal, setLocal } from "apis/persist"

const onEnter = onKeyPress(["Enter"])

const titleCase = str =>
  str.toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")

class MyMazdaAddVehiclFlow extends Component {
  constructor() {
    super()
    this.state = {
      inputValue: "",
      isFetching: false,
      searchError: null,
      foundVehicle: {}
    }
    bindAll(
      [
        "linkVehicle",
        "renderSearchError",
        "renderLinkSuccess",
        "handleInputChange",
        "handleSearch",
        "renderVinSearch",
        "renderConfirmVehicle"
      ],
      this
    )
  }

  componentDidMount() {
    // clear localStorage
    removeLocal("addRemoveVehicleLastStep")
  }

  handleSearch(callback, errorCallback) {
    if (!this.state.inputValue.length) return
    this.setState({ isFetching: true })
    queryMyMazdaVinRego(this.props.vehicleSearchEndPoint, this.state.inputValue)
      .then(({ errorCode, errorMessage, ...foundVehicle }) => {
        this.setState({ isFetching: false, foundVehicle }, callback)
      })
      .catch(error => {
        this.setState({ isFetching: false, searchError: this.props.errorMessage }, errorCallback)
      })
  }

  linkVehicle(setStep) {
    const {
      foundVehicle: { vin, nameplate, rego }
    } = this.state

    this.setState({ isFetching: true })
    linkMyMazdaVinRego(this.props.linkVehicleEndPoint, vin)
      .then(({ statusCode }) => {
        const messages = {
          // statusCode 202 means vehicle transfer has been requested
          202: MYMAZDA_ERRORS.VEHICLE_ADD_TRANSFER_REQUESTED({ nameplate, rego }),
          // statusCode 200 indicates a successful link
          200: MYMAZDA_ERRORS.VEHICLE_ADD_SUCCESS({ nameplate, rego })
        }

        this.props.flashMessage(
          messages[statusCode || 200],
          "SUCCESS"
        )
        if (statusCode === 200) {
          // force page reload to show updated navigation
          isLocalStorageSupported() && setLocal("addRemoveVehicleLastStep", JSON.stringify(true))
          setStep("renderLinkSuccess")
        } else {
          // refresh route will only update page content not navigation
          // transfer requested vehicles don't show in nav
          this.props.refreshCurrentRoute()
          this.props.onClose()
        }
      })
      .catch(error => {
        this.props.flashMessage(
          MYMAZDA_ERRORS.VEHICLE_ADD_FAILED({ nameplate, rego, error: error.message }),
          "ERROR"
        )
        this.props.onClose()
      })
  }

  renderVinSearch({ setStep, nextStep }) {
    const {
      compact,
      addVehicleHeading,
      addVehicleDescriptionExtended,
      addSearchButtonText
    } = this.props

    const handelSearch = () => {
      if (!this.state.inputValue) return
      return this.handleSearch(
        () => setStep("renderConfirmVehicle"),
        () => setStep("renderSearchError")
      )
    }
    return (
      <div style={styles.slide(compact)}>
        <H3 withDetails>{addVehicleHeading}</H3>
        <Space />
        <WysiwygContent>
          <p>{addVehicleDescriptionExtended}</p>
        </WysiwygContent>
        <InputText
          label={"Enter your registration or VIN"}
          disabled={this.state.isFetching}
          onKeyPress={onEnter(handelSearch)}
          value={this.state.inputValue}
          onChange={({ target: { value } }) => this.setState({ inputValue: value })} />
        <Button
          fullWidth
          loading={this.state.isFetching}
          disabled={!this.state.inputValue}
          styleName={"black"}
          onClick={handelSearch}>
          {addSearchButtonText}
        </Button>
        <ExpandableArea>
          {({ RenderArea, toggleExpanded }) => (
            <div style={{ margin: "2em 0 0" }}>
              <Hyperlink onClick={toggleExpanded} styleName="black">
                {this.props.vinHitLabel}
              </Hyperlink>
              <RenderArea>
                <p style={{ fontSize: "90%", color: "#aaaaaa", padding: "1em 0" }}>
                  {this.props.vinHintDescription}
                </p>
              </RenderArea>
            </div>
          )}
        </ExpandableArea>
      </div>
    )
  }
  renderConfirmVehicle({ setStep }) {
    const {
      compact, addConfirmHeading, addConfirmButtonText, addVehicleConfirmationDescription
    } = this.props
    const {
      foundVehicle: {
        nameplate, modelDescription, vin, rego, colour
      }
    } = this.state
    return (
      <div style={styles.slide(compact)}>
        <span style={styles.backLink} onClick={() => setStep("renderVinSearch")}>
          {"<"} Go Back
        </span>
        <H3 withDetails>{addConfirmHeading}</H3>
        <Space />
        <div style={{ marginBottom: "2em" }}>
          <Summary
            disableBase
            items={[
              {
                label: "Model",
                value: `${titleCase(modelDescription || "") || nameplate}`
              },
              {
                label: "Registration",
                value: rego || "Contact Dealer"
              },
              {
                label: "VIN",
                value: vin
              },
              colour && {
                label: "Exterior",
                value: colour
              }
            ].filter(Boolean)} />
        </div>
        <div style={{ borderTop: `1px solid ${COLOR_PALETTE.GRAY_LIGHT}`, margin: "2em 0 2.5em" }} />
        <WysiwygContent xmlString={addVehicleConfirmationDescription} />
        <Button
          fullWidth
          loading={this.state.isFetching}
          styleName={"black"}
          onClick={() => {this.linkVehicle(setStep)}}
          gtm={{
            category: "MyMazda - Manage Vehicles",
            action: `Add vehicle - clicked "${addConfirmButtonText.toUpperCase()}"`
          }}>
          {addConfirmButtonText}
        </Button>
      </div>
    )
  }
  renderSearchError({ setStep }) {
    const { compact, retrySearchButtonText } = this.props
    return (
      <div style={styles.slide(compact)}>
        <H3 withDetails>Vehicle not found</H3>
        <Space />
        <Space />
        <AlertList data={this.state.searchError} />
        <Button
          fullWidth
          styleName={"black"}
          onClick={() => {
            this.setState({ inputValue: "", searchError: null })
            setStep(0)
          }}>
          {"<"} {retrySearchButtonText}
        </Button>
      </div>
    )
  }
  renderLinkSuccess() {
    const { compact } = this.props
    const {
      foundVehicle: { nameplate, rego }
    } = this.state

    return (
      <div style={styles.slide(compact)}>
        <TickModalIcon style={{ width: "2.25em", height: "2.25em", margin: "0 auto 1em" }} />
        <H3>Vehicle added</H3>
        <WysiwygContent xmlString={`${MYMAZDA_ERRORS.VEHICLE_ADD_SUCCESS({ nameplate, rego })}`} theme={"light"} style={{margin: "1em 0"}} />
        <Button
          onClick={() => { window.location.reload()}}
          fullWidth
          styleName="black"
        >
          Continue
        </Button>
      </div>
    )
  }
  render() {
    const {
      renderVinSearch,
      renderConfirmVehicle,
      renderLinkSuccess,
      renderSearchError
    } = this
    return (
      <SteppedFlow>
        {{
          renderVinSearch,
          renderConfirmVehicle,
          renderLinkSuccess,
          renderSearchError
        }}
      </SteppedFlow>
    )
  }
}

const styles = {
  slide: compact => ({
    padding: compact ? "0" : "3em",
    textAlign: "center"
  }),
  backLink: {
    ...BASE_HEADING,
    display: "block",
    textAlign: "left",
    marginTop: "-2em",
    marginLeft: "-2em",
    marginBottom: "2em",
    fontSize: "80%",
    cursor: "pointer",
    color: COLOR_PALETTE.GRAY_DARK,
    transition: "color .25s",
    ":hover": {
      color: COLOR_PALETTE.GRAY_DARKER
    }
  }
}

MyMazdaAddVehiclFlow.propTypes = {
  compact: PropTypes.bool,
  errorMessage: PropTypes.string,
  addVehicleConfirmationDescription: PropTypes.string,
  addVehicleDescriptionExtended: PropTypes.string,
  addVehicleHeading: PropTypes.string,
  addSearchButtonText: PropTypes.string,
  retrySearchButtonText: PropTypes.string,
  addConfirmHeading: PropTypes.string,
  addConfirmButtonText: PropTypes.string,
  vehicleSearchEndPoint: PropTypes.string,
  vinHitLabel: PropTypes.string,
  vinHintDescription: PropTypes.string,
  linkVehicleEndPoint: PropTypes.string,
  refreshCurrentRoute: PropTypes.func,
  flashMessage: PropTypes.func,
  onClose: PropTypes.func
}

const mapDispatchToProps = dispatch => ({
  flashMessage: (...args) => dispatch(actionCreators.addFlashMessage(...args)),
  refreshCurrentRoute: () => dispatch(actionCreators.refreshCurrentRoute())
})

export default withRadium(connect(null, mapDispatchToProps), DetectCompact())(props => (
  <Modal open width={"650px"} handleClose={props.onClose}>
    <MyMazdaAddVehiclFlow {...props} />
  </Modal>
))



// WEBPACK FOOTER //
// ./src/containers/mymazda/manage-vehicle-flows/add-vehicle.jsx